import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';
import { Alert, Button, Checkbox, CheckListItem, Empty, FieldWrapper, Filter, FilterButton, Input, Label, LayoutSidebar, SectionHeader, Select, Tooltip } from 'components';
import { theme, toRem } from 'design';
import { useDebounce } from 'hooks';
import { selectSiteSettings } from 'store/Site/selectors';
import { selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorPOSResults, setPOSResults } from 'store/TipCalculator';
import { ChangeLogAction, ICalculatorRules, ICheck, IEmployerSite, IPOSResults, IPOSSegment, IPOSSegmentTotals, IPOSTicket, IPOSTicketSplitOverride, ITipCalculatorChangeLogEntry, TicketOverrideType } from 'types';
import { forceRender } from 'utils';
import { buildCheckChangeLogEntries, buildSortedChecksList, calculateTicketTotals } from 'utils/tipCalculator';
import InfiniteScroll from '../../../../components/InfiniteScroll/InfiniteScroll';
import { MPContent } from '../../components';
import Summary from '../components/Checks/Summary/Summary';
import { CalculateProcessingModal } from '../components/modals/CalculateProcessingModal';
import { EditCheckAmounts } from '../components/modals/EditCheckAmountsModal/EditCheckAmountsModal';
import { IgnoreChecksModal } from '../components/modals/IgnoreChecksModal/IgnoreChecksModal';
import { SplitCheckModal } from '../components/modals/SplitCheckModal/SplitCheckModal';
import { UndoCheckChangesModal } from '../components/modals/UndoCheckChangesModal/UndoCheckChangesModal';

interface IChecksProps {
  currentCalculatorRules: ICalculatorRules;
  currentSite: IEmployerSite;
  pageHeaderContent: React.ReactNode;
  sidebarOpen?: boolean;
  onBack: () => void;
  onExpand: () => void;
}

interface IFilters {
  checkNumber: string | undefined;
  employeeName: string | undefined;
  checkTypes: string[] | undefined;
}

const StickyHeader = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  background-color: ${theme.color.white};
  top: ${toRem(66)};
  z-index: ${theme.zIndex.sidebar - 1};
  width: 100%;
  background: linear-gradient(to bottom, ${theme.color.white} 90%, transparent 100%);
  padding: ${toRem(16)} ${toRem(theme.layout.gutter.small)} 0;

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(16)} ${toRem(theme.layout.gutter.small)} 0 ${toRem(theme.layout.gutter.med)};
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: ${toRem(16)};
  gap: ${toRem(12)};
  div {
    gap: ${toRem(12)};
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    flex-wrap: wrap-reverse;
  }
`;

const SelectedItemControl = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${toRem(4)};
  margin-top: ${toRem(0.5)};

  label {
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.medium};
    color: ${theme.color.neutral[500]};
  }
`;

const ClearSelected = styled.button`
  font-size: ${toRem(10)};
  font-weight: 500;
  color: ${theme.color.primary[600]};
  margin-top: ${toRem(1)};

  &:hover {
    cursor: pointer;
    color: ${theme.color.primary[800]};
  }
`;

const ITEM_LIST_OFFSET = 10;

const getSegmentMatch = (posDataClone: IPOSResults, selectedCheck: ICheck) =>
  posDataClone.data
    .map((item) => item.segments.find((segment) => segment.tickets.find((ticket) => ticket.ticketId === selectedCheck.ticketId)))
    .find((segment) => segment?.tickets.find((ticket) => ticket.ticketId === selectedCheck.ticketId));

const getTicketMatch = (matchSegment: IPOSSegment, selectedCheck: ICheck) => matchSegment?.tickets.find((ticket) => ticket.ticketId === selectedCheck.ticketId);

const calculateSegmentTotalsForOverride = (segment: IPOSSegment, ticket: IPOSTicket, operator: '+' | '-'): IPOSSegmentTotals => {
  const overrideTotals = calculateTicketTotals([ticket]);
  const multiplier = operator === '+' ? 1 : -1;

  if (overrideTotals.gross > 0) {
    return {
      ...segment.totals,
      autoGratuity: {
        gross: segment.totals.autoGratuity.gross + multiplier * overrideTotals.autoGratuity.gross,
        net: segment.totals.autoGratuity.net + multiplier * overrideTotals.autoGratuity.net,
        cash: segment.totals.autoGratuity.cash + multiplier * overrideTotals.autoGratuity.cash,
        creditCard: segment.totals.autoGratuity.creditCard + multiplier * overrideTotals.autoGratuity.creditCard,
        creditCardFee: segment.totals.autoGratuity.creditCardFee + multiplier * overrideTotals.autoGratuity.creditCardFee,
      },
      tip: {
        gross: segment.totals.tip.gross + multiplier * overrideTotals.tip.gross,
        net: segment.totals.tip.net + multiplier * overrideTotals.tip.net,
        cash: segment.totals.tip.cash + multiplier * overrideTotals.tip.cash,
        creditCard: segment.totals.tip.creditCard + multiplier * overrideTotals.tip.creditCard,
        creditCardFee: segment.totals.tip.creditCardFee + multiplier * overrideTotals.tip.creditCardFee,
      },
      gross: segment.totals.gross + multiplier * overrideTotals.gross,
      net: segment.totals.net + multiplier * overrideTotals.net,
    };
  }

  return segment.totals;
};

const Checks = ({ currentSite, currentCalculatorRules, onBack, onExpand, pageHeaderContent, sidebarOpen }: IChecksProps) => {
  const dispatch = useDispatch();
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [filters, setFilters] = useState<IFilters>();
  const [editCheck, setEditCheck] = useState<ICheck>();
  const [maxOffset, setMaxOffset] = useState<number>(ITEM_LIST_OFFSET);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingBulkAction, setIsLoadingBulkAction] = useState<boolean>(false);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);

  const [checkList, setCheckList] = useState<ICheck[]>();
  const [filteredCheckList, setFilteredCheckList] = useState<ICheck[]>();
  const { timezone: currentSiteTimezone, holdback: currentSiteHoldbacks, tipCalculatorPercentFee: currentSiteTipCalcPercentFee } = useSelector(selectSiteSettings()) || {};
  const paymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentSetSegments = paymentSet?.segments;
  const paymentDate = paymentSet?.date;

  const posDataResults = useSelector(selectCalculatorPOSResults());
  const [posDataClone, setPOSDataClone] = useState(structuredClone(posDataResults));
  const dirty = useMemo(() => !isEqual(posDataResults, posDataClone), [posDataResults, posDataClone]);

  // Change log entries
  const changeLogEntries = useSelector(selectCalculatorChangeLog());
  const [currentChangeLogEntries, setCurrentChangeLogEntries] = useState<ITipCalculatorChangeLogEntry[]>(changeLogEntries);

  const filterChecks = useCallback((checks: ICheck[], filters?: IFilters) => {
    let filteredChecks: ICheck[] = [...checks];
    // filter by check number
    if (filters?.checkNumber) filteredChecks = filteredChecks.filter((check) => check.displayId.toLowerCase().startsWith(filters?.checkNumber.trim().toLowerCase()));

    // filter by employee name
    if (filters?.employeeName) filteredChecks = filteredChecks.filter((check) => check.employeeName.toLowerCase().indexOf(filters?.employeeName.trim().toLowerCase()) > -1);

    // filter by check type
    if (filters?.checkTypes) filteredChecks = filteredChecks.filter((check) => filters?.checkTypes.includes(check.override?.type));

    // Set select all based on the filtered checks
    if (filteredChecks.length > 0 && filteredChecks.every((check) => check.selected)) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }

    setFilteredCheckList(filteredChecks);
  }, []);

  const selectedChecks = useMemo(() => (checkList || []).filter((check) => check.selected), [checkList]);

  useEffect(() => {
    const checkList = buildSortedChecksList(posDataClone);
    setIsLoading(true);
    setCheckList(checkList);
    filterChecks(checkList, filters); // default the filters list
    setIsLoading(false);
  }, [posDataClone, paymentDate]);

  useEffect(() => {
    if (checkList) {
      setMaxOffset(ITEM_LIST_OFFSET); // reset the maxOffset when changing filters
      filterChecks(checkList, filters);
    }
  }, [filters]);

  const filterByCheckNumber = (value: string) => {
    let checkNumber = value;
    if (value.charAt(0) === '#') checkNumber = value.slice(1); // remove # if it is the first character in the string.
    setFilters({ ...filters, checkNumber: !isEmpty(checkNumber) ? checkNumber : undefined });
  };
  const onFilterCheckByNumber = useDebounce(filterByCheckNumber);

  const filterByEmployeeName = (value: string) => {
    setFilters({ ...filters, employeeName: !isEmpty(value) ? value : undefined });
  };
  const onFilterEmployeeName = useDebounce(filterByEmployeeName);

  const filterByCheckType = (value: { value: string; label: string }[]) => {
    const checkTypes = !isEmpty(value) ? value.map((item) => item.value) : undefined;
    setFilters({ ...filters, checkTypes });
  };
  const onFilterCheckType = useDebounce(filterByCheckType);

  const totalTipsReceived = useMemo(() => checkList?.reduce((acc, check) => acc + check.totals.gross, 0), [checkList]);

  const checksCountText = useMemo(() => {
    if (filteredCheckList && checkList && filters && filteredCheckList.length !== checkList.length) return `${filteredCheckList?.length} of ${checkList?.length}`;
    return checkList?.length.toString() || '0';
  }, [checkList, filteredCheckList]);

  const undoCheckOverride = (check: ICheck, posData: IPOSResults, changeLogEntries: ITipCalculatorChangeLogEntry[]) => {
    const matchSegment = getSegmentMatch(posData, check);
    const matchTicket = getTicketMatch(matchSegment, check);

    if (matchTicket?.override) {
      // Add the sum of the check back to the overall totals
      Object.assign(matchSegment, { totals: calculateSegmentTotalsForOverride(matchSegment, matchTicket, '+') });

      // remove the overides from the ticket
      delete matchTicket.override;

      return {
        posData,
        changeLogEntries: buildCheckChangeLogEntries(ChangeLogAction.UNDO_OVERRIDE, changeLogEntries, posDataResults, check),
      };
    }
    return { posData, changeLogEntries };
  };

  const onUndoCheckOverride = async (checks: ICheck[]) => {
    setIsLoadingBulkAction(true);
    await forceRender();

    let updatedPosData = posDataClone;
    let updatedChangeLogEntries = currentChangeLogEntries;

    // Process all checks in a single batch
    checks.forEach((check) => {
      // Only undo overrides if the check has an override
      if (check.override) {
        const result = undoCheckOverride(check, updatedPosData, updatedChangeLogEntries);
        updatedPosData = result.posData;
        updatedChangeLogEntries = result.changeLogEntries;
      }
    });

    // Make a single state update with all changes
    setCurrentChangeLogEntries(updatedChangeLogEntries);
    setPOSDataClone(structuredClone(updatedPosData));

    // Hide modal after all checks are processed
    setSelectAllChecked(false);
    setIsLoadingBulkAction(false);
    hideUndoChangesWarningModal();
  };

  const splitCheck = (check: ICheck, override: IPOSTicketSplitOverride, posData: IPOSResults, changeLogEntries: ITipCalculatorChangeLogEntry[]) => {
    const matchSegment = getSegmentMatch(posData, check);
    const matchTicket = getTicketMatch(matchSegment, check);

    if (matchSegment && matchTicket) {
      // Check if the ticket has previously contained an override to prevent double subtractions
      const isOverride = !!matchTicket.override;

      // Add the overrides to the ticket
      Object.assign(matchTicket, { override });

      // Update segment totals
      if (!isOverride) Object.assign(matchSegment, { totals: calculateSegmentTotalsForOverride(matchSegment, matchTicket, '-') });

      return {
        posData,
        changeLogEntries: buildCheckChangeLogEntries(ChangeLogAction.SPLIT, changeLogEntries, posDataResults, check),
      };
    }
    return { posData, changeLogEntries };
  };

  const handleSplit = (checks: ICheck[], overrides: IPOSTicketSplitOverride[]) => {
    let updatedPosData = posDataClone;
    let updatedChangeLogEntries = currentChangeLogEntries;

    // Process all checks in a single batch
    checks.forEach((check, i) => {
      const result = splitCheck(check, overrides[i], updatedPosData, updatedChangeLogEntries);
      updatedPosData = result.posData;
      updatedChangeLogEntries = result.changeLogEntries;
    });

    // Make a single state update with all changes
    setCurrentChangeLogEntries(updatedChangeLogEntries);
    setPOSDataClone(structuredClone(updatedPosData));

    // Hide the split check modal only after all checks have been processed
    setSelectAllChecked(false);
    hideSplitCheckModal();
  };

  const handleEditCheckAmounts = useCallback(
    (check: ICheck) => {
      const matchSegment = getSegmentMatch(posDataClone, check);
      const matchTicket = getTicketMatch(matchSegment, check);

      if (matchSegment && matchTicket) {
        // Recalculate the segment totals based on the new cash amount
        Object.assign(matchSegment, {
          totals: {
            ...matchSegment.totals,
            tip: {
              ...matchSegment.totals.tip,
              gross: matchSegment.totals.tip.gross - matchTicket.totals.tip.cash + check.totals.tip.cash,
              net: matchSegment.totals.tip.net - matchTicket.totals.tip.cash + check.totals.tip.cash,
              cash: matchSegment.totals.tip.cash - matchTicket.totals.tip.cash + check.totals.tip.cash,
            },
            gross: matchSegment.totals.gross - matchTicket.totals.tip.cash + check.totals.tip.cash,
            net: matchSegment.totals.net - matchTicket.totals.tip.cash + check.totals.tip.cash,
          },
        });

        // Update the ticket totals to the new cash amount
        Object.assign(matchTicket, { totals: check.totals });

        setCurrentChangeLogEntries(buildCheckChangeLogEntries(ChangeLogAction.CASH_EDITED, currentChangeLogEntries, posDataResults, check));
        setPOSDataClone(structuredClone(posDataClone));

        setSelectAllChecked(false);
        hideEditCheckAmountsModal();
      }
    },
    [posDataClone]
  );

  const [showAbandonChangesModal, hideAbandonChangesModal] = useModal(
    () => (
      <Alert
        data-cy="abandon-changes-alert"
        title="Abandon Changes?"
        message="You have unsaved changes that haven't been applied to your calculations. Are you sure you want to discard edits?"
        onLeftAction={hideAbandonChangesModal}
        onRightAction={onBack}
        leftActionLabel="Cancel"
        rightActionLabel="Abandon Changes"
        rightActionAppearance="destructive"
        isActive
      />
    ),
    [onBack]
  );

  const ignoreCheck = (check: ICheck, posData: IPOSResults, changeLogEntries: ITipCalculatorChangeLogEntry[]) => {
    const matchSegment = getSegmentMatch(posData, check);
    const matchTicket = getTicketMatch(matchSegment, check);

    if (matchSegment && matchTicket) {
      // Check if the ticket has previously contained an override to prevent double subtractions
      const isOverride = !!matchTicket.override;

      // Add the overrides to the ticket
      Object.assign(matchTicket, { override: { type: TicketOverrideType.IGNORE } });

      // Update segment totals
      if (!isOverride) Object.assign(matchSegment, { totals: calculateSegmentTotalsForOverride(matchSegment, matchTicket, '-') });

      return {
        posData,
        changeLogEntries: buildCheckChangeLogEntries(ChangeLogAction.IGNORE, changeLogEntries, posDataResults, check),
      };
    }
    return { posData, changeLogEntries };
  };

  const handleIgnoreCheck = async (checks: ICheck[]) => {
    setIsLoadingBulkAction(true);
    await forceRender();

    let updatedPosData = posDataClone;
    let updatedChangeLogEntries = currentChangeLogEntries;

    // Process all checks in a single batch
    checks.forEach((check) => {
      const result = ignoreCheck(check, updatedPosData, updatedChangeLogEntries);
      updatedPosData = result.posData;
      updatedChangeLogEntries = result.changeLogEntries;
    });

    // Make a single state update with all changes
    setCurrentChangeLogEntries(updatedChangeLogEntries);
    setPOSDataClone(structuredClone(updatedPosData));

    // Hide modal and reset loading state
    setSelectAllChecked(false);
    setIsLoadingBulkAction(false);
    hideIgnoreCheckModal();
  };

  const [showIgnoreCheckModal, hideIgnoreCheckModal] = useModal(
    () => <IgnoreChecksModal checks={selectedChecks} onClose={hideIgnoreCheckModal} onApply={() => handleIgnoreCheck(selectedChecks)} isLoading={isLoadingBulkAction} />,
    [selectedChecks, isLoadingBulkAction, handleIgnoreCheck]
  );

  const [showUndoChangesWarningModal, hideUndoChangesWarningModal] = useModal(
    () => <UndoCheckChangesModal checks={selectedChecks} onClose={hideUndoChangesWarningModal} onApply={() => onUndoCheckOverride(selectedChecks)} isLoading={isLoadingBulkAction} />,
    [selectedChecks, isLoadingBulkAction, onUndoCheckOverride]
  );

  const [showIntegrationDataProcessingModal, hideIntegrationDataProcessingModal] = useModal(
    () => (
      <CalculateProcessingModal
        tipCalcRules={currentCalculatorRules}
        onClose={hideIntegrationDataProcessingModal}
        businessDate={paymentDate}
        siteId={currentSite.id}
        siteHoldbackSettings={currentSiteHoldbacks}
        segments={paymentSetSegments}
        posDataResults={posDataClone}
        creditCardPercentFee={currentSiteTipCalcPercentFee}
        changeLogEntries={currentChangeLogEntries}
        onSuccess={() => dispatch(setPOSResults(posDataClone))}
      />
    ),
    [currentCalculatorRules, paymentDate, currentSite, currentSiteHoldbacks, paymentSet, posDataClone]
  );

  const primarySummaryAction = useCallback(
    (device: 'mobile' | 'desktop', allowRecalculate: boolean) => (
      <Button
        data-cy="labor-data-next-button"
        label="Recalculate"
        type="button"
        appearance="primary"
        disabled={!posDataClone.data.length || !dirty || totalTipsReceived <= 0 || !allowRecalculate}
        size={device === 'mobile' ? 'small' : 'large'}
        onClick={() => showIntegrationDataProcessingModal()}
      />
    ),
    [posDataClone, dirty]
  );

  const secondarySummaryAction = useCallback(
    (device: 'mobile' | 'desktop') => <Button data-cy="checks-back-button" label="Back" type="button" appearance="white" size={device === 'mobile' ? 'small' : 'large'} onClick={dirty ? showAbandonChangesModal : onBack} />,
    [onBack, dirty]
  );

  const loadMoreChecks = useCallback(async () => {
    if (filteredCheckList.length > maxOffset) {
      setMaxOffset(maxOffset + ITEM_LIST_OFFSET);
    }
  }, [filteredCheckList, maxOffset]);

  const onEditAmounts = (check: ICheck) => {
    setEditCheck(check);
    showEditCheckAmountsModal();
  };

  const onSelectCheck = (selectedCheck: ICheck, checked: boolean) => {
    const selectedTicketId = selectedCheck.ticketId.trim();

    const filteredChecks = filteredCheckList.map((check) => (check.ticketId.trim() === selectedTicketId ? { ...check, selected: checked } : check));

    // Update all checks to be selected or not
    const checks = checkList.map((check) => (check.ticketId.trim() === selectedTicketId ? { ...check, selected: checked } : check));

    // Set select all based on the filtered checks
    if (filteredChecks.every((check) => check.selected)) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }

    setFilteredCheckList(filteredChecks);
    setCheckList(checks);
  };

  const onToggleSelectAllChecks = (checked: boolean, clearAll = false) => {
    setSelectAllChecked(checked);
    const filteredTicketIds = new Set();
    const filteredChecks = filteredCheckList.map((check) => {
      filteredTicketIds.add(check.ticketId.trim()); // Get the ticketIds of filtered checks for efficient lookup
      return { ...check, selected: checked }; // update all
    });

    // update only the checks that are in the filtered list
    const checks = checkList.map((check) => (filteredTicketIds.has(check.ticketId.trim()) || clearAll ? { ...check, selected: checked } : check));

    setFilteredCheckList(filteredChecks);
    setCheckList(checks);
  };

  const ignoredChecks = useMemo(() => checkList?.filter((check) => check.override?.type === TicketOverrideType.IGNORE), [checkList]);
  const splitChecks = useMemo(() => checkList?.filter((check) => check.override?.type === TicketOverrideType.SPLIT), [checkList]);
  const appliedFilters = Object.values(filters || {}).filter((value) => value);

  const [showSplitCheckModal, hideSplitCheckModal] = useModal(() => <SplitCheckModal checks={selectedChecks} onClose={hideSplitCheckModal} onSave={handleSplit} />, [selectedChecks, handleSplit]);

  const [showEditCheckAmountsModal, hideEditCheckAmountsModal] = useModal(
    () => <EditCheckAmounts check={editCheck} onClose={hideEditCheckAmountsModal} timeZone={currentSiteTimezone} onSave={handleEditCheckAmounts} />,
    [editCheck, currentSiteTimezone, handleEditCheckAmounts]
  );

  const selectedItemControl = () => {
    if (selectedChecks.length) {
      return (
        <SelectedItemControl>
          <Label>{`${selectedChecks.length} Check${selectedChecks.length === 1 ? '' : 's'} Selected`}</Label>
          <ClearSelected onClick={() => onToggleSelectAllChecks(false, true)}>Clear Selected</ClearSelected>
        </SelectedItemControl>
      );
    }
    return null;
  };

  const disableSplit = !selectedChecks.some((check) => check.totals.gross > 0);
  const disableIgnore = selectedChecks.length === 0;
  const disableUndo = !selectedChecks.some((check) => check.override && [TicketOverrideType.SPLIT, TicketOverrideType.IGNORE].includes(check.override.type));

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}
          <StickyHeader>
            <SectionHeader data-cy="checks-header" title={`Checks (${checksCountText})`} subtitle={selectedItemControl()} isLoading={isLoading}>
              <Controls data-cy="checks-header-controls">
                <FilterButton isLoading={isLoading} filterActive={filterActive} appliedFilters={appliedFilters.length} onClick={() => setFilterActive(!filterActive)} />
                <div>
                  <Button data-cy="split-button" isLoading={isLoading} appearance="white" label="Split" onClick={showSplitCheckModal} size="small" icon="Split" disabled={disableSplit} />
                  <Button data-cy="ignore-button" isLoading={isLoading} appearance="white" label="Ignore" onClick={showIgnoreCheckModal} size="small" icon="No" disabled={disableIgnore} />
                  <Button data-cy="undo-button" isLoading={isLoading} label="Undo" onClick={showUndoChangesWarningModal} size="small" icon="Retry" appearance="white" disabled={disableUndo} />
                  <Tooltip id="select-all-checks-tooltip" place="top">
                    Select all
                  </Tooltip>
                  <Checkbox
                    id="checks-select-all"
                    data-cy="checks-select-all"
                    size="large"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggleSelectAllChecks(e.target.checked)}
                    checked={selectAllChecked}
                    data-tooltip-id="select-all-checks-tooltip"
                  />
                </div>
              </Controls>
            </SectionHeader>
            <Filter isVisible={filterActive} data-cy="filter-section">
              <FieldWrapper label="Check Number">
                <Input id="checkNumber" type="text" data-cy="check-number-input" placeholder="Search..." maxLength={20} autoComplete="off" onChange={(e) => onFilterCheckByNumber(e.target.value)} />
              </FieldWrapper>
              <FieldWrapper label="Employee Name">
                <Input id="employeeName" type="text" data-cy="employee-name-input" placeholder="Search..." maxLength={50} autoComplete="off" onChange={(e) => onFilterEmployeeName(e.target.value)} />
              </FieldWrapper>
              <FieldWrapper label="Check Type">
                <Select
                  options={[
                    { label: 'Split', value: TicketOverrideType.SPLIT },
                    { label: 'Ignored', value: TicketOverrideType.IGNORE },
                  ]}
                  values={[]}
                  multi
                  onChange={(selected) => onFilterCheckType(selected)}
                  data-cy="check-type-select"
                />
              </FieldWrapper>
            </Filter>
          </StickyHeader>
          <MPContent data-cy="checks-list" $smallGutter>
            {!isLoading && filteredCheckList && (
              <>
                {!filteredCheckList.length ? (
                  <Empty container containerHeight={250} icon="Ticket" message="No checks found" />
                ) : (
                  <InfiniteScroll fetchMore={loadMoreChecks}>
                    {filteredCheckList.slice(0, maxOffset).map((check, idx) => (
                      <CheckListItem
                        check={check}
                        key={`check-list-item-${idx}`}
                        timeZone={currentSiteTimezone}
                        onEditAmounts={onEditAmounts}
                        categoryGroups={currentCalculatorRules.categoryGroups || []}
                        creditCardFee={currentSiteTipCalcPercentFee}
                        onSelect={onSelectCheck}
                      />
                    ))}
                  </InfiniteScroll>
                )}
              </>
            )}
          </MPContent>
        </>
      }
      sidebar={
        <Summary
          date={paymentDate}
          onExpand={onExpand}
          primaryActionBtn={primarySummaryAction}
          secondaryActionBtn={secondarySummaryAction}
          sidebarOpen={sidebarOpen}
          totalTipsReceived={totalTipsReceived}
          isLoading={isLoading}
          ignoredChecks={ignoredChecks}
          splitChecks={splitChecks}
          changeLogEntries={currentChangeLogEntries}
        />
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default Checks;
